/**
 * Turkish formatting.
 *
 * `tr-TR` throughout: comma as decimal separator, dotted thousands, Turkish
 * month names. Using the browser's locale would produce "0.83" for a user
 * reading "0,83" everywhere else in the page.
 */

const LOCALE = "tr-TR";

const percentFormatter = new Intl.NumberFormat(LOCALE, {
  style: "percent",
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat(LOCALE);

const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** An em dash, used wherever a value genuinely does not exist. */
export const ABSENT = "—";

/**
 * A 0-1 score as a percentage.
 *
 * Returns `ABSENT` rather than "0%" for null. A missing measurement and a
 * measurement of zero are different facts, and rendering them identically is
 * the same mistake as conflating `unknown` with `insufficient`.
 */
export function formatScore(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return ABSENT;
  return percentFormatter.format(value);
}

export function formatDecimal(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return ABSENT;
  return decimalFormatter.format(value);
}

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return ABSENT;
  return integerFormatter.format(value);
}

/**
 * Independent viewpoints. The backend computes these as a saturating sum, so
 * the value is fractional - 2,5 is a real and meaningful answer, and rounding
 * it to 3 would overstate the evidence.
 */
export function formatViews(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return ABSENT;
  return Number.isInteger(value) ? integerFormatter.format(value) : decimalFormatter.format(value);
}

export function formatDateTime(iso: string | null | undefined): string {
  const date = parse(iso);
  return date ? dateTimeFormatter.format(date) : ABSENT;
}

export function formatDate(iso: string | null | undefined): string {
  const date = parse(iso);
  return date ? dateFormatter.format(date) : ABSENT;
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return ABSENT;
  if (bytes < 1024) return `${integerFormatter.format(bytes)} B`;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${decimalFormatter.format(value)} ${BYTE_UNITS[unit]}`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return ABSENT;
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return minutes > 0
    ? `${minutes} dk ${String(rest).padStart(2, "0")} sn`
    : `${rest} sn`;
}

export function formatDimensions(
  width: number | null | undefined,
  height: number | null | undefined,
): string {
  if (!width || !height) return ABSENT;
  return `${integerFormatter.format(width)} × ${integerFormatter.format(height)}`;
}

/**
 * A short, non-identifying handle for a UUID.
 *
 * Used only where a row needs to be distinguishable at a glance. It is never
 * the sole way to identify anything, and it is never presented as a name.
 */
export function shortId(id: string): string {
  return id.slice(0, 8);
}
